import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Loader2, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import {
  fetchZajetosc,
  przyjmijStanOdWszystkich,
  stanPrzyjeciaWszystkich,
  zlecOdczytZajetosci,
  type StanPrzyjeciaAutomatu,
} from '@/lib/machines-api';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/lib/i18n';
import { RozjazdStanu } from '@/components/RozjazdStanu';
import { useToast } from '@/components/ui/toast';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ZAJĘTOŚĆ AUTOMATÓW — I TO, ZA ILE PORTAL ZA NIĄ RĘCZY.
 *
 *  ⚠️⚠️ POWSTAŁO Z AWARII IKEA (18-21.09.2026). Portal pokazywał zajętość 62,4 %, w bazie automatu
 *  było 80,0 %, na masterze 81,1 %. SPS-y zgadzały się ze sobą, a portal z żadnym z nich — ale
 *  SAMA LICZBA NIE BYŁA PROBLEMEM. Problemem było to, że nikt nie miał jak zobaczyć, że jest
 *  nieaktualna: ekran podawał ją tym samym krojem i tym samym tonem, co liczbę świeżą.
 *
 *  ⚠️⚠️ DLATEGO KAŻDY WIERSZ MA DWIE CZĘŚCI, A NIE JEDNĄ: ile wynosi i ile z niej portal
 *  potwierdza. Wiersz zamrożony to taki, na który portal ma niewykonaną zmianę — jego meldunki
 *  o stanie są wtedy odrzucane, więc zapisane `QTYCURRENT` pochodzi sprzed tej zmiany.
 *
 *  ⚠️ NIE PORÓWNUJEMY TU PORTALU Z MASTEREM I NIE UDAJEMY, ŻE UMIEMY: odczyt na żądanie wycina
 *  stan magazynowy po stronie agenta, więc portal nie ma dziś skąd wziąć `QTYCURRENT` z cudzej
 *  bazy. Trójstronne porównanie wchodzi razem ze stronicowanym odczytem.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function ZajetoscAutomatow({ groupId }: { groupId: string }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const [zlecam, setZlecam] = useState(false);

  const dane = useQuery({
    queryKey: ['zajetosc', groupId],
    queryFn: () => fetchZajetosc(groupId),
    enabled: groupId !== '',
    refetchInterval: 60_000,
  });

  const automaty = dane.data?.automaty ?? [];
  /** ⚠️ Nikt nie pytał ≠ wszyscy się zgadzają — to rozróżnienie jest tu całą wartością ekranu. */
  const ktosPytal = dane.data?.odczytKiedy !== null && dane.data?.odczytKiedy !== undefined;

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════
   *  „PRZYJMIJ STAN OD WSZYSTKICH AUTOMATÓW" — zamówione 24.09.2026 po zgłoszeniu z IKEA.
   *
   *  ⚠️⚠️ DWA KLIKNIĘCIA, jak kroki naprawy przy jednym automacie: to nadpisuje liczby, na których
   *  stoją raporty i Vending+. Zdanie przed potwierdzeniem mówi, co się stanie i czego NIE zrobi.
   *
   *  ⚠️ PORTAL PRZYJMUJE SAM, zaraz po odczycie każdego automatu — ekran tylko odpytuje stan.
   *  Zamknięcie ekranu niczego nie przerywa.
   * ══════════════════════════════════════════════════════════════════════════════════════════
   */
  const { isAdmin } = useAuth();
  const [pytaWszystkie, setPytaWszystkie] = useState(false);
  const [zlecamWszystkie, setZlecamWszystkie] = useState(false);
  const [zlecenie, setZlecenie] = useState<{ id: string; bezWlasciciela: number[]; zaStare: string[] } | null>(null);
  const stanWszystkich = useQuery({
    queryKey: ['przyjecie-wszystkich', groupId, zlecenie?.id],
    queryFn: () => stanPrzyjeciaWszystkich(groupId, zlecenie!.id),
    enabled: zlecenie !== null,
    refetchInterval: (q) => (q.state.data?.zakonczone === true ? false : 5_000),
  });
  const zakonczone = stanWszystkich.data?.zakonczone === true;
  /** ⚠️ Po przyjęciu liczby portalu są inne — lista zajętości wyżej musi się przeliczyć. */
  useEffect(() => {
    if (zakonczone) void qc.invalidateQueries({ queryKey: ['zajetosc', groupId] });
  }, [zakonczone, qc, groupId]);

  const przyjmijWszystkie = async (): Promise<void> => {
    setZlecamWszystkie(true);
    try {
      const w = await przyjmijStanOdWszystkich(groupId);
      setZlecenie({ id: w.zlecenieId, bezWlasciciela: w.bezWlasciciela, zaStare: w.zaStare });
      setPytaWszystkie(false);
      toast.success(t('zajetosc.wszystkie.zlecono').replace('{n}', String(w.automatow)));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setZlecamWszystkie(false);
    }
  };

  const opisPrzyjecia = (a: StanPrzyjeciaAutomatu): string => {
    if (a.stan === 'CZEKA') return t('zajetosc.wszystkie.CZEKA');
    if (a.stan === 'BLAD') return t('zajetosc.wszystkie.BLAD').replace('{powod}', a.powod ?? '');
    if (a.stan === 'NIE') return t('zajetosc.wszystkie.NIE').replace('{powod}', a.powod ?? '');
    const czesci = [
      (a.zmienionych ?? 0) > 0
        ? t('zajetosc.wszystkie.PRZYJETO').replace('{n}', String(a.zmienionych))
        : t('zajetosc.wszystkie.ZGODNE'),
    ];
    if ((a.zamrozonych ?? 0) > 0) czesci.push(t('zajetosc.wszystkie.zamrozonych').replace('{n}', String(a.zamrozonych)));
    if ((a.nieznanych ?? 0) > 0) czesci.push(t('zajetosc.wszystkie.nieznanych').replace('{n}', String(a.nieznanych)));
    return czesci.join(' · ');
  };

  const zapytaj = async (): Promise<void> => {
    setZlecam(true);
    try {
      const w = await zlecOdczytZajetosci(groupId);
      toast.success(t('zajetosc.zlecono').replace('{n}', String(w.wezly.length)));
      /** ⚠️ Węzły pominięte przez starą wersję muszą paść z nazwy — inaczej ich brak w wyniku czyta się jak zgoda. */
      if (w.zaStare.length > 0) toast.toast(t('zajetosc.zaStare').replace('{wezly}', w.zaStare.join(', ')));
      await qc.invalidateQueries({ queryKey: ['zajetosc', groupId] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setZlecam(false);
    }
  };

  if (automaty.length === 0) return null;

  return (
    <section className="space-y-2 rounded-lg border border-border p-3">
      <h2 className="text-sm font-medium">{t('zajetosc.tytul')}</h2>
      <p className="text-[11px] text-muted-foreground">{t('zajetosc.poCo')}</p>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" disabled={zlecam} onClick={() => void zapytaj()}>
          {zlecam ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
          {t('zajetosc.zapytaj')}
        </Button>
        {dane.data?.odczytCzeka === true && (
          <span className="text-[11px] text-muted-foreground">{t('zajetosc.czekam')}</span>
        )}
        {isAdmin && !pytaWszystkie && (
          <Button size="sm" variant="outline" disabled={zlecamWszystkie} onClick={() => setPytaWszystkie(true)}>
            <Download className="size-4" />
            {t('zajetosc.wszystkie.przycisk')}
          </Button>
        )}
      </div>

      {/* ── PRZYJĘCIE OD WSZYSTKICH: zdanie, potwierdzenie, potem stan każdego automatu ──── */}
      {pytaWszystkie && (
        <div className="space-y-1.5 rounded-md border border-border p-2 text-[11px]">
          <p>{t('zajetosc.wszystkie.opis')}</p>
          <div className="flex gap-2">
            <Button size="sm" disabled={zlecamWszystkie} onClick={() => void przyjmijWszystkie()}>
              {zlecamWszystkie && <Loader2 className="size-4 animate-spin" />}
              {t('stanRoz.potwierdz')}
            </Button>
            <Button size="sm" variant="outline" disabled={zlecamWszystkie} onClick={() => setPytaWszystkie(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}
      {zlecenie !== null && (
        <div className="space-y-0.5 rounded-md border border-border p-2 text-[11px]">
          <p className="flex items-center gap-1.5 font-medium">
            {!zakonczone && <Loader2 className="size-3 animate-spin" />}
            {zakonczone ? t('zajetosc.wszystkie.gotowe') : t('zajetosc.wszystkie.wToku')}
          </p>
          {(stanWszystkich.data?.automaty ?? []).map((a) => (
            <p key={a.vmid} className={a.stan === 'BLAD' || a.stan === 'NIE' ? 'text-[var(--warning)]' : undefined}>
              <span className="font-medium">{a.nazwa ?? `VMID ${a.vmid}`}</span> — {opisPrzyjecia(a)}
            </p>
          ))}
          {/* ⚠️ Pominięte automaty z nazwy — ich brak na liście czytałby się jak „zgodne". */}
          {zlecenie.bezWlasciciela.length > 0 && (
            <p className="text-[var(--warning)]">
              {t('zajetosc.wszystkie.bezWlasciciela').replace('{automaty}', zlecenie.bezWlasciciela.join(', '))}
            </p>
          )}
          {zlecenie.zaStare.length > 0 && (
            <p className="text-[var(--warning)]">
              {t('zajetosc.wszystkie.zaStare').replace('{wezly}', zlecenie.zaStare.join(', '))}
            </p>
          )}
        </div>
      )}

      {/*
        ⚠️⚠️ „NIKT NIE PYTAŁ" MUSI BYĆ NAPISANE. Liczby wyżej są wtedy WYŁĄCZNIE liczbami portalu —
        a przez trzy dni awarii to właśnie brak drugiej strony wyglądał jak zgoda obu.
      */}
      {!ktosPytal && <p className="text-[11px] text-[var(--warning)]">{t('zajetosc.nikt')}</p>}

      {/* ⚠️ Granica liczenia wypisana, gdy ją przekroczono: liczba zaniżona podana jako pewna byłaby gorsza niż brak. */}
      {dane.data?.niepelne === true && (
        <p className="text-[11px] text-[var(--warning)]">{t('zajetosc.niepelne')}</p>
      )}

      <ul className="space-y-1">
        {automaty.map((a) => (
          <li key={a.machineId} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-xs">
            <span className="min-w-48 font-medium">
              {a.nazwa} <span className="text-muted-foreground">({a.vmid})</span>
            </span>

            {/*
              ⚠️ `null` TO NIE ZERO. Automat bez planogramu nie jest „pusty w 0 %" — o jego
              zapełnieniu nie wiadomo nic, a zero wygląda jak zmierzona pustka.
            */}
            <span className="tabular-nums font-semibold">
              {a.procent === null ? t('zajetosc.brakPlanogramu') : `${a.procent}%`}
            </span>

            <span className="tabular-nums text-muted-foreground">
              {t('zajetosc.pozycji').replace('{n}', String(a.pozycji))}
            </span>

            {/*
              ⚠️⚠️ TO JEST TA LINIJKA, DLA KTÓREJ CAŁY TEN PANEL POWSTAŁ. Bez niej 62,4 % wygląda
              jak zmierzony stan automatu, a jest starym stanem podanym jako bieżący.
            */}
            {a.zamrozonych > 0 ? (
              <span className="tabular-nums font-medium text-[var(--warning)]">
                {t('zajetosc.zamrozonych')
                  .replace('{n}', String(a.zamrozonych))
                  .replace('{p}', String(a.udzialZamrozonych))}
              </span>
            ) : (
              a.pozycji > 0 && <span className="text-[var(--success)]">{t('zajetosc.pewna')}</span>
            )}

            {/*
              ⚠️ WŁAŚCICIEL PRZY LICZBIE, bo to JEDYNY węzeł, który zna stan tego automatu naprawdę.
              Gdy milczy, liczba portalu nie ma skąd się odświeżyć — i to jest inny powód
              nieaktualności niż zamrożenie, więc mówimy o nim osobno.
            */}
            {a.wlasciciel === null ? (
              <span className="text-[var(--warning)]">{t('zajetosc.brakWlasciciela')}</span>
            ) : (
              <span className="font-mono text-[11px] text-muted-foreground">
                {a.wlasciciel}
                {a.minutOdKontaktuWlasciciela === null
                  ? ` · ${t('zajetosc.nigdyNieOdpowiedzial')}`
                  : a.minutOdKontaktuWlasciciela > 20
                    ? ` · ${t('zajetosc.cisza').replace('{n}', String(a.minutOdKontaktuWlasciciela))}`
                    : ''}
              </span>
            )}
            {/*
              ══════════════════════════════════════════════════════════════════════════════════
               CO MÓWIĄ BAZY — trzecia strona miary.

               ⚠️⚠️ RÓŻNICĘ PODAJEMY W PUNKTACH, BEZ PROGU I BEZ OCENY. Stan magazynowy zmienia się
               między odczytami (ktoś wydaje towar w tej samej minucie), więc „równe co do joty"
               nie jest wymogiem, a portal nie ma prawa rozstrzygać za człowieka, ile punktów
               to jeszcze szum. 62,4 kontra 80,0 rozpozna sam.
              ══════════════════════════════════════════════════════════════════════════════════
            */}
            {a.wezly.length > 0 && (
              <span className="basis-full pl-2 text-[11px] text-muted-foreground">
                {t('zajetosc.wgBaz')}{' '}
                {a.wezly
                  .map((w) => {
                    const roznica =
                      w.procent === null || a.procent === null
                        ? null
                        : Math.round((w.procent - a.procent) * 10) / 10;
                    return `${w.nodeId} ${w.procent === null ? '—' : `${w.procent}%`}${
                      roznica === null || roznica === 0 ? '' : ` (${roznica > 0 ? '+' : ''}${roznica} pkt)`
                    }`;
                  })
                  .join(' · ')}
              </span>
            )}
            {/*
              ⚠️⚠️ ROZJAZD WIERSZ PO WIERSZU PRZY KAŻDYM AUTOMACIE. Procent mówi, ŻE coś się
              rozjechało; dopiero wiersze mówią CO i KTO jest do tyłu. Bez tego drugiego kroku
              liczba jest alarmem bez adresu — a tak wyglądał ten temat od piątku.
            */}
            <RozjazdStanu groupId={groupId} vmid={a.vmid} />
          </li>
        ))}
      </ul>
    </section>
  );
}
